import type { QueryClient } from '@tanstack/react-query';

/**
 * Query-key factory.
 *
 * Every key starts with the active clinic id. That is not cosmetic: TanStack
 * Query caches across a clinic switch, and a key of `['patients', page]` would
 * hand clinic B's receptionist clinic A's patient list out of the cache for as
 * long as it stayed fresh. Scoping the key makes the two caches disjoint, and
 * `AuthProvider` additionally clears the cache on switch — belt and braces,
 * because this one is a patient-data leak rather than a stale number.
 */

export const queryKeys = {
  me: () => ['auth', 'me'] as const,
  deviceSessions: () => ['auth', 'sessions'] as const,
  supportAccess: (clinicId: string | null, page: number) =>
    ['auth', 'support-access', clinicId, page] as const,

  clinic: (clinicId: string | null) => ['clinic', clinicId] as const,

  notifications: {
    all: (clinicId: string | null) => ['notifications', clinicId] as const,
    unreadCount: (clinicId: string | null) => ['notifications', clinicId, 'unread-count'] as const,
    list: (clinicId: string | null, page: number, pageSize: number) =>
      ['notifications', clinicId, 'list', page, pageSize] as const,
  },

  /**
   * The dashboard is branch-aware (its agenda, stock alerts and revenue follow
   * the switcher): every tile key ends with the branch segment. It defaults to
   * `'all'` so a caller that has not adopted the switcher yet still gets a
   * valid key — and `all(clinicId)` stays the prefix of every tile.
   */
  dashboard: {
    all: (clinicId: string | null) => ['dashboard', clinicId] as const,
    appointments: (clinicId: string | null, dayKey: string, branch: string = 'all') =>
      ['dashboard', clinicId, 'appointments', dayKey, branch] as const,
    followUps: (clinicId: string | null, withinDays: number, branch: string = 'all') =>
      ['dashboard', clinicId, 'follow-ups', withinDays, branch] as const,
    pendingConsents: (clinicId: string | null, dayKey: string, branch: string = 'all') =>
      ['dashboard', clinicId, 'pending-consents', dayKey, branch] as const,
    inventoryAlerts: (clinicId: string | null, branch: string = 'all') =>
      ['dashboard', clinicId, 'inventory-alerts', branch] as const,
    revenue: (clinicId: string | null, period: string, branch: string = 'all') =>
      ['dashboard', clinicId, 'revenue', period, branch] as const,
  },

  /** The caller's welcome/tour state and guidance preferences (`GET /guidance`). */
  guidance: {
    state: (clinicId: string | null) => ['guidance', clinicId, 'state'] as const,
  },

  /** «پیشنهادها» — the smart guide. */
  guide: {
    all: (clinicId: string | null) => ['guide', clinicId] as const,
    tips: (clinicId: string | null) => ['guide', clinicId, 'tips'] as const,
    occasions: (clinicId: string | null, days: number) =>
      ['guide', clinicId, 'occasions', days] as const,
    clinicSettings: (clinicId: string | null) => ['guide', clinicId, 'clinic-settings'] as const,
  },

  /** «ثبت اطلاعات از روی عکس». */
  aiImport: {
    all: (clinicId: string | null) => ['ai-import', clinicId] as const,
    entitlement: (clinicId: string | null) => ['ai-import', clinicId, 'entitlement'] as const,
    jobs: (clinicId: string | null, params: Record<string, unknown>) =>
      ['ai-import', clinicId, 'jobs', params] as const,
    job: (clinicId: string | null, jobId: string) => ['ai-import', clinicId, 'job', jobId] as const,
    rows: (clinicId: string | null, jobId: string, pageId: string | null, filter: string | null) =>
      ['ai-import', clinicId, 'job', jobId, 'rows', pageId, filter] as const,
  },

  /* ---------------------------------------------------------------------- */
  /* Pricing build (WS-0) — declared once so the panel streams never collide */
  /* ---------------------------------------------------------------------- */

  billing: {
    /** `GET /billing/plan-access` — the lock family's one read (C4). */
    planAccess: (clinicId: string | null) => ['billing', clinicId, 'plan-access'] as const,
    raas: {
      summary: (clinicId: string | null, periodKey: string | null) =>
        ['billing', clinicId, 'raas', 'summary', periodKey] as const,
      charges: (clinicId: string | null, periodKey: string, params: Record<string, unknown>) =>
        ['billing', clinicId, 'raas', 'charges', periodKey, params] as const,
    },
    /** Money-back eligibility and the existing request. */
    refund: (clinicId: string | null) => ['billing', clinicId, 'refund'] as const,
    treatedPatients: (clinicId: string | null, months: number) =>
      ['billing', clinicId, 'treated-patients', months] as const,
  },

  /**
   * The branch catalogue itself. NOT branch-aware (it is what the switcher
   * reads); every branch write invalidates `all(clinicId)`.
   */
  branches: {
    all: (clinicId: string | null) => ['branches', clinicId] as const,
    /** `GET /branches/mine` — the switcher and every «شعبه» picker. */
    mine: (clinicId: string | null) => ['branches', clinicId, 'mine'] as const,
    list: (clinicId: string | null, includeArchived: boolean) =>
      ['branches', clinicId, 'list', includeArchived] as const,
    usage: (clinicId: string | null) => ['branches', clinicId, 'usage'] as const,
    slug: (clinicId: string | null, slug: string, exceptBranchId: string | null) =>
      ['branches', clinicId, 'slug', slug, exceptBranchId ?? 'new'] as const,
  },

  /** Inventory keys owned by the panel-wide factory (the feature keeps its own in `inventory/api.ts`). */
  inventory: {
    transfers: (clinicId: string | null, branch: string, params: Record<string, unknown>) =>
      ['inventory', clinicId, 'transfers', branch, params] as const,
  },

  integrations: {
    keys: (clinicId: string | null) => ['integrations', clinicId, 'keys'] as const,
    webhooks: (clinicId: string | null) => ['integrations', clinicId, 'webhooks'] as const,
    deliveries: (clinicId: string | null, endpointId: string, params: Record<string, unknown>) =>
      ['integrations', clinicId, 'deliveries', endpointId, params] as const,
    activity: (clinicId: string | null, keyId: string, page: number) =>
      ['integrations', clinicId, 'activity', keyId, page] as const,
  },
} as const;

/**
 * The branch segment of a branch-aware query key: the active branch id, or
 * `'all'` for «همهٔ شعبه‌ها». Keys that vary by the switcher adopt it
 * (panel-branches-ops), so switching branch never serves another branch's
 * cached rows.
 */
export function branchKey(activeBranchId: string | null | undefined): string {
  return activeBranchId ? activeBranchId : 'all';
}

/**
 * ── WHICH CACHES THE BRANCH SWITCHER TOUCHES ────────────────────────────────
 *
 * The first segment of every query key whose answer depends on the active
 * branch: the calendar and its queues, sessions, the till and expenses and
 * P&L and reports (all under `accounting`), stock and lots and transfers
 * (`inventory`), and the dashboard. Each of those features puts
 * {@link branchKey} into its keys, so a switch lands on a different key — and
 * for the one refetch that matters, the switch INVALIDATES exactly these roots
 * for the active clinic.
 *
 * Never `queryClient.clear()`: that is the CLINIC switch's tool (a tenant
 * boundary — `auth/clinic-switcher.tsx`). A branch is not one: the patient
 * file, the price list, staff and settings are clinic-wide (owner round 5),
 * and dropping them would cost every screen a reload for nothing.
 *
 * NOT branch-aware, deliberately: `patients` (the file is ONE file across every
 * branch — a doctor at A must see the filler injected at B), `branches`,
 * `settings-section`, `staff`, procedures and packages (one price list per
 * clinic), `guidance`, `billing`.
 */
export const BRANCH_AWARE_ROOTS: readonly string[] = Object.freeze([
  'calendar',
  'sessions',
  'accounting',
  'inventory',
  'dashboard',
]);

/** Whether a query key belongs to a branch-aware cache of `clinicId`. */
export function isBranchAwareKey(key: readonly unknown[], clinicId: string | null): boolean {
  return typeof key[0] === 'string' && BRANCH_AWARE_ROOTS.includes(key[0]) && key[1] === clinicId;
}

/** Refetch every branch-aware query of the active clinic — and nothing else. */
export function invalidateBranchAwareQueries(
  queryClient: QueryClient,
  clinicId: string | null,
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isBranchAwareKey(query.queryKey, clinicId),
  });
}
